import { GMI, STATE, COUNTRIES } from "../../../constants";
import replacement from "../../../services/gma/replacement";
import { types } from "../../../utils/js/types";
import { batchActions } from "../../../utils/redux";
import request from "../../request";

const { REPLACEMENT } = STATE.UI;

/**
 * @typedef {"country" | "country_subdivision" | "driver_license_number"} FieldNames
 */

/** @type {Record<FieldNames, string[]>} */
const FIELD_PATHS = {
  country: REPLACEMENT.DRIVER.SEARCH_BY_DRIVER_LICENSE.COUNTRY,
  country_subdivision:
    REPLACEMENT.DRIVER.SEARCH_BY_DRIVER_LICENSE.COUNTRY_SUBDIVISION,
  driver_license_number:
    REPLACEMENT.DRIVER.SEARCH_BY_DRIVER_LICENSE.DRIVER_LICENSE_NUMBER
};

/**
 * Sets a field inside searchByDriverLicenseState
 * @private
 * @param {FieldNames} field
 * @param {unknown} value
 * @returns {Object} {type: string, path: [], value: object}
 */
function setField(field, value) {
  const path = FIELD_PATHS[field];

  return {
    type: GMI.SET_GMI_STATE,
    path,
    value
  };
}

/**
 * Clears a field inside searchByDriverLicenseState
 * @private
 * @param {FieldNames} field
 * @returns {Object} {type: string, path: [], value: object}
 */
function clearField(field) {
  const path = FIELD_PATHS[field];

  return {
    type: GMI.SET_GMI_STATE,
    path,
    value: null
  };
}

/**
 * Setter for the ui.replacement.driver.searchByDriverLicense data
 * @private
 * @param {Record<FieldNames, unknown>} fields - Key-value pairs of fields and values to set
 * @returns {Object} Batched action that sets each passed field
 */
const setState = (fields = {}) => {
  const batchedActions = [];

  Object.entries(fields).forEach(([key, value]) => {
    if (FIELD_PATHS[key] && value) {
      batchedActions.push(setField(key, value));
    }
  });

  return batchActions(...batchedActions);
};

/**
 * Unsetter for the ui.replacement.driver.searchByDriverLicense data
 * @private
 * @returns {Object} Unsetter action object
 */
function clearState() {
  const batchedActions = [];

  Object.keys(FIELD_PATHS).forEach((field) => {
    batchedActions.push(clearField(field));
  });

  return batchActions(...batchedActions);
}

export const searchByDriverLicenseState = {
  /**
   * @name set
   * @desc A global setter for ui.replacement.driver.searchByDriverLicense data
   * @param {String} [field]
   * @param {Object} [value]
   * @returns {Function}
   * @memberof actions.replacement.driver
   * @example
   *
   * // Set searchByDriverLicense country
   * store.dispatch(actions.replacement.driver.searchByDriverLicenseState.set('country', ''));
   *
   * // Set searchByDriverLicense state
   * store.dispatch(actions.replacement.driver.searchByDriverLicenseState.set(null, {}));
   *
   */
  set(field = null, value = {}) {
    return (dispatch) => {
      if (!types(field).isString) return dispatch(setState(value));
      if (FIELD_PATHS[field]) return dispatch(setField(field, value));
    };
  },

  /**
   * @name unset
   * @desc A global unsetter for ui.replacement.driver.searchByDriverLicense data
   * @param {FieldNames | null} [field]
   * @param {unknown} [args]
   * @returns {Function}
   * @memberof actions.replacement
   * @example
   *
   * // Unset searchByDriverLicense country
   * store.dispatch(actions.replacement.driver.searchByDriverLicenseState.unset('country'));
   *
   * // Unset searchByDriverLicense state
   * store.dispatch(actions.replacement.driver.searchByDriverLicenseState.unset());
   *
   */
  unset(type = null) {
    return (dispatch) => {
      if (!types(type).isString) return dispatch(clearState());
      if (FIELD_PATHS[type]) return dispatch(clearField(type));
    };
  }
};

/**
 * @desc gets data from `replacement.driver_info.license_profile` to be saved to `gmi.ui.reservations.driver.searchByDriverLicense` state
 * @param {Immutable.Map<any, any>} session - GMA session
 * @returns {Record<FieldNames, unknown>}
 */
export function getSearchByDriverLicenseState(session) {

  
  // We check if any loyalty data exists within the driver info from the current search so
  // we can know if form data should be overridden or not.
  // We need second path in case we show associate modal and call different endpoint which return info 
  // about loyalty data in different part of session. 
  const hasLoyaltyData = !!session.getIn(["replacement", "driver_info", "profile", "basic_profile", "loyalty_data", "loyalty_code"]) || 
  !!session.getIn(["replacement", "ticket", "profile_details", "loyalty_data", "loyalty_code"]);

  // If we don't have loyalty data attached to the search, ignore hydration as we want to keep current UI state.
  if (!hasLoyaltyData) return;

  const licenseProfile = session
    .getIn(["replacement", "driver_info", "license_profile"])
    ?.toJS();

  if (!licenseProfile) return;

  return {
    country: licenseProfile?.country_code,
    country_subdivision: licenseProfile?.country_subdivision_code,
    driver_license_number: licenseProfile?.license_number
  };
}

/**
 * Calls Search By Driver License service
 * @param {Object} object
 * @example store.dispatch(actions.replacement.driver.searchByDriverLicense());
 * @returns {Function} Dispatched service action
 */
export function searchByDriverLicense() {
  return (dispatch, getState) => {
	const state = getState();

	let data = state
  	.getIn(REPLACEMENT.DRIVER.SEARCH_BY_DRIVER_LICENSE.PATH)
  	.toJS();
    
  // Check if the country is "GBR" and update the key  
	if (data.country === COUNTRIES.GBR) {
  	data.issuing_authority = data.country_subdivision;
  	delete data.country_subdivision;
	}

	return dispatch(
  	request({
    	service: replacement.searchByDriverLicense,
    	data
  	})
	);
  };
}
